import { EditableCard } from "../../../components/editableCard.component";
import { AppRouteUi } from "../../../config/appRoute";
import { useParams } from "react-router";
import { ApiSchemaActivity } from "../../../models/api/activity";
import { useEffect, useState } from "react";
import { Pagination, Table } from "rsuite";
import { PaginationEntriesInput } from "../../../components/paginationEntries";
import { useAppDispatch, useAppSelector } from "../../../core/redux";
import { ActionsCaregiverActivityTable } from "../../../core/redux/slices/sliceCaregiverActivityTable";
import { ApiPatientActivity } from "../../../core/apis/activity";
import {
  DateCell,
  ImageCell,
  SeeTasksCell,
  TextCell,
} from "../../../components/tableItem.component";
import { ApiSchemaCaregiver } from "../../../models/api/caregiver";

type TableData = ApiSchemaActivity & {
  name: string;
};

type Props = {
  data: ApiSchemaCaregiver | undefined;
  isValidating: boolean;
};

const { Column, HeaderCell, Cell } = Table;

export const CaregiverActivityLogCard = ({
  data: caregiverData,
  isValidating: caregiverValidating,
}: Props) => {
  const { caregiverId } = useParams<{ caregiverId: string }>();
  const { data, isValidating } = ApiPatientActivity.Caregiver.Hook(
    caregiverId,
    caregiverData?.companyId
  );
  const [tableData, setTableData] = useState<TableData[]>([]);
  const { pageNumber, pageSize, isLoading } = useAppSelector(
    (state) => state.caregiverActivityTable
  );

  const dispatch = useAppDispatch();

  useEffect(() => {
    dispatch(ActionsCaregiverActivityTable.goToPage(1));
    dispatch(ActionsCaregiverActivityTable.setEntryCount(10));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(
    () =>
      setTableData(
        data?.list.map((activity, index) => ({
          sl: (pageNumber - 1) * pageSize + index + 1,
          name: activity.patient?.firstName + " " + activity.patient?.lastName,
          photo: activity.patient?.profilePic,
          tasks: activity.taskList,
          phone: activity.patient?.phoneNumber,
          ...activity,
        })) ?? []
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  return (
    <EditableCard
      link={`${AppRouteUi.Caregiver.Root()}/update/${caregiverId}`}
      title="Activity Log"
      isValidating={isValidating || caregiverValidating}
    >
      <div>
        <div className="p-2 d-flex align-item-center">
          <div className="align-self-center me-2">Show</div>
          <PaginationEntriesInput
            onChange={(value) =>
              dispatch(
                ActionsCaregiverActivityTable.setEntryCount(value as number)
              )
            }
          />
          <div className="align-self-center ms-2">entries</div>
        </div>

        <Table
          autoHeight
          data={tableData}
          loading={isValidating || isLoading}
          renderEmpty={() => (
            <div className="d-flex justify-content-center text-muted align-items-center h-100 text-center p-5">
              <h3 className="fw-light">No Data Available</h3>
            </div>
          )}
        >
          <Column align="center" width={50}>
            <HeaderCell className="fw-bold text-black">
              <span title="S/L">S/L</span>
            </HeaderCell>
            <TextCell dataKey="sl" />
          </Column>

          <Column align="center" width={70}>
            <HeaderCell className="fw-bold text-black">
              <span title="Photo">Photo</span>
            </HeaderCell>
            <ImageCell dataKey="photo" />
          </Column>

          <Column flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="Patient Name">Patient Name</span>
            </HeaderCell>
            <TextCell dataKey="name" />
          </Column>

          <Column flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="Phone Number">Phone Number</span>
            </HeaderCell>
            <TextCell dataKey="phone" />
          </Column>

          <Column flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="Start Time">Start Time</span>
            </HeaderCell>
            <DateCell dataKey="startTime" time />
          </Column>

          <Column flexGrow={1}>
            <HeaderCell className="fw-bold text-black">
              <span title="End Time">End Time</span>
            </HeaderCell>
            <DateCell dataKey="endTime" time />
          </Column>

          <Column>
            <HeaderCell className="fw-bold text-black">
              <span title="Task List">Task List</span>
            </HeaderCell>
            <Cell>
              {(rowData: TableData) => (
                <SeeTasksCell
                  taskDetailsList={rowData.patient?.taskDetailsList}
                />
              )}
            </Cell>
          </Column>
        </Table>

        {data && (
          <div className="d-flex justify-content-end mt-3">
            <Pagination
              total={data.totalCount}
              pages={Math.ceil(data.totalCount / pageSize)}
              maxButtons={6}
              next={true}
              prev={true}
              activePage={pageNumber}
              onSelect={(page) =>
                dispatch(ActionsCaregiverActivityTable.goToPage(page))
              }
            />
          </div>
        )}
      </div>
    </EditableCard>
  );
};
